import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import ApiConfig from 'src/config/api-config';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';

export type CheckinStatus = 'pending_confirmation' | 'confirmed' | 'declined' | 'completed';
export type CheckinPreference = 'online_zoom' | 'in_person' | 'phone';

export type CheckinProgress = '1' | '2' | '3' | '4' | '5' | '6';
export const CHECKIN_PROGRESS = {
  PENDING: '1',
  PROPOSED_NEW_TIME: '2',
  CONFIRMED: '3',
  ATTENDED: '4',
  DECLINED: '5',
  CANCELLED: '6',
} as const;

export type CheckinSelectTimeType = 'morningList' | 'daytimeList' | 'eveningList';
export type CheckinType = 'online' | 'inPerson';

export interface CheckinAttachment {
  file_name: string;
  mime_type: string;
  size_bytes: number;
  url: string;
}

export interface CheckinActionItem {
  id: string;
  text: string;
  completed: boolean;
}

export interface TranscriptHighlight {
  time: string;
  text: string;
}

export interface AiProgressSummary {
  performance_score: number;
  great_form_count: number;
  good_form_count: number;
  needs_focus_count: number;
  skipped_count: number;
  workouts_completed: number;
  workouts_total: number;
  last_activity_date: string;
  last_activity_relative: string;
  form_concern: string;
}

export interface CheckinItem {
  id: string;
  checkin_id?: string;
  message_id?: string;
  chat_id?: string;
  client_uid: string;
  trainer_uid: string;
  client_name?: string;
  client_email?: string;
  client_avatar?: string | null;
  program_id?: string;
  date: string;
  time: string;
  datetime_utc?: string;
  timezone?: string;
  preference?: CheckinPreference;
  checkinType?: CheckinType;
  // Mobile Checkin lifecycle fields
  checkinProgress?: CheckinProgress;
  checkinDate?: string;
  checkinTime?: string;
  checkinSelectTimeType?: CheckinSelectTimeType;
  checkinNewDate?: string;
  checkinNewTime?: string;
  checkinNewSelectTimeType?: CheckinSelectTimeType;
  checkinNewType?: CheckinType;
  checkinNewSuggest?: boolean;
  checkinDeclinedReason?: string;
  zoomMeetingUrl?: string;
  zoomMeetingId?: string;
  zoomMeetingPwd?: string;
  isAppoint?: boolean;
  appointUId?: string;
  legacy_status?: 'open' | 'close';
  duration_mins?: number;
  attendance?: 'attended' | 'missed';
  send_zoom_link_on_confirm?: boolean;
  zoom_link?: string;
  meeting_id?: string;
  passcode?: string;
  status: CheckinStatus;
  ai_progress_summary?: AiProgressSummary;
  ai_summary?: string[];
  transcript_highlights?: TranscriptHighlight[];
  full_transcript?: string[];
  action_items?: CheckinActionItem[];
  last_reminder_sent_at?: string;
  created_at?: string | { toDate: () => Date } | null;
  updated_at?: string | { toDate: () => Date } | null;
  status_updated_at?: string | { toDate: () => Date } | null;
  status_updated_by?: string;
  notes?: string;
  attachments?: CheckinAttachment[];
  is_read?: boolean;
  read_time?: string | null;
  read_by?: string;
  suggested_date?: string;
  suggested_time?: string;
  suggested_datetime_utc?: string;
  suggestion_notes?: string;
  decline_reason?: string;
  // Ready-to-render presentation fields computed on backend
  header_title?: string;
  status_badge?: {
    label: string;
    className: string;
    hasDot: boolean;
  };
  formatted_date?: string;
  formatted_time?: string;
  formatted_subtitle?: string;
  formatted_sent_time?: string;
  client_initials?: string;
  preference_label?: string;
}

export interface GetCheckinsParams {
  search?: string;
  clientId?: string;
  status?: string;
  sort?: string;
  limit?: number;
  cursor?: string;
}

export interface GetCheckinsResponse {
  items: CheckinItem[];
  hasMore: boolean;
  nextCursor: string | null;
}

export interface ConfirmCheckinPayload {
  checkinId: string;
  chatId?: string;
  sendZoomLinkOnConfirm?: boolean;
}

export interface CompleteCheckinPayload {
  checkinId: string;
  chatId?: string;
  attendance?: 'attended' | 'missed';
  durationMins?: number;
  notes?: string;
  actionItems?: CheckinActionItem[];
}

export interface ZoomMeetingStatusResponse {
  success: boolean;
  has_zoom_meeting: boolean;
  meeting_id?: string;
  actual_duration_mins?: number | null;
  has_recording?: boolean;
  has_transcript?: boolean;
  transcript_turns_count?: number;
  highlights_count?: number;
  transcript_preview?: string;
  message?: string;
}

export interface SuggestCheckinPayload {
  checkinId: string;
  chatId?: string;
  suggestedDate: string;
  suggestedTime: string;
  suggestedSelectTimeType?: CheckinSelectTimeType;
  suggestedType?: CheckinType;
  timezone?: string;
  notes?: string;
}

export interface DeclineCheckinPayload {
  checkinId: string;
  chatId?: string;
  reason?: string;
}

export interface SendReminderPayload {
  checkinId: string;
  chatId?: string;
}

export interface UpdateActionItemsPayload {
  checkinId: string;
  chatId?: string;
  actionItems: CheckinActionItem[];
}

const getAuthToken = async (): Promise<string> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error('ERR_UNAUTHORIZED');
  return token;
};

// API Functions
export const fetchTrainerCheckins = async (
  params: GetCheckinsParams,
): Promise<GetCheckinsResponse> => {
  const token = await getAuthToken();

  const queryParams = new URLSearchParams();
  if (params.search) queryParams.append('search', params.search);
  if (params.clientId && params.clientId !== 'all') queryParams.append('clientId', params.clientId);
  if (params.status && params.status !== 'all') queryParams.append('status', params.status);
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
  if (params.cursor) queryParams.append('cursor', params.cursor);

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CHECKINS}?${queryParams.toString()}`;

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch check-ins: ${response.status}`);
  }

  return response.json();
};

export const fetchTrainerCheckinDetails = async (
  checkinId: string,
  targetDate?: string,
  chatId?: string,
): Promise<{ success: boolean; checkin: CheckinItem }> => {
  const token = await getAuthToken();

  const queryParams = new URLSearchParams();
  queryParams.append('checkin_id', checkinId);
  if (targetDate) queryParams.append('date', targetDate);
  if (chatId) queryParams.append('chat_id', chatId);

  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_CHECKIN_DETAILS}?${queryParams.toString()}`;

  const response = await fetch(apiUrl, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(
      errData?.error?.message || `Failed to fetch check-in details: ${response.status}`,
    );
  }

  return response.json();
};

export const markTrainerCheckinRead = async (payload: {
  checkinId: string;
  targetDate?: string;
  chatId?: string;
}): Promise<{ success: boolean; checkin_id: string }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_MARK_CHECKIN_READ}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      date: payload.targetDate,
      chat_id: payload.chatId,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to mark check-in read: ${response.status}`);
  }

  return response.json();
};

export const confirmTrainerCheckin = async (
  payload: ConfirmCheckinPayload,
): Promise<{ success: boolean; checkin: CheckinItem }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_CONFIRM_CHECKIN}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
      send_zoom_link_on_confirm: payload.sendZoomLinkOnConfirm,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to confirm check-in: ${response.status}`);
  }

  return response.json();
};

export const completeTrainerCheckin = async (
  payload: CompleteCheckinPayload,
): Promise<{ success: boolean; checkin: CheckinItem; message?: string }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_COMPLETE_CHECKIN}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
      attendance: payload.attendance || 'attended',
      duration_mins: payload.durationMins || 30,
      notes: payload.notes || '',
      action_items: payload.actionItems || [],
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to complete check-in: ${response.status}`);
  }

  return response.json();
};

export const fetchZoomMeetingStatus = async (
  checkinId: string,
  chatId?: string,
): Promise<ZoomMeetingStatusResponse> => {
  try {
    const token = await getAuthToken();
    const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_GET_ZOOM_MEETING_STATUS}`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        checkin_id: checkinId,
        chat_id: chatId,
      }),
    });

    if (!response.ok) {
      return { success: false, has_zoom_meeting: false };
    }

    return await response.json();
  } catch {
    return { success: false, has_zoom_meeting: false };
  }
};

export const suggestTrainerCheckin = async (
  payload: SuggestCheckinPayload,
): Promise<{ success: boolean; checkin: CheckinItem }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SUGGEST_CHECKIN}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
      suggested_date: payload.suggestedDate,
      suggested_time: payload.suggestedTime,
      suggested_select_time_type: payload.suggestedSelectTimeType,
      suggested_type: payload.suggestedType,
      timezone: payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      notes: payload.notes || '',
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to suggest new time: ${response.status}`);
  }

  return response.json();
};

export const declineTrainerCheckin = async (
  payload: DeclineCheckinPayload,
): Promise<{ success: boolean; checkin: CheckinItem }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_DECLINE_CHECKIN}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
      reason: payload.reason || '',
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to decline check-in: ${response.status}`);
  }

  return response.json();
};

export const sendTrainerCheckinReminder = async (
  payload: SendReminderPayload,
): Promise<{ success: boolean; message: string; last_reminder_sent_at?: string }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_SEND_CHECKIN_REMINDER}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to send reminder: ${response.status}`);
  }

  return response.json();
};

export const updateTrainerCheckinActionItems = async (
  payload: UpdateActionItemsPayload,
): Promise<{ success: boolean; action_items: CheckinActionItem[]; message: string }> => {
  const token = await getAuthToken();
  const apiUrl = `${ApiConfig.CLOUD_FUNCTIONS_BASE_URL}${ApiConfig.TRAINER_UPDATE_CHECKIN_ACTION_ITEMS}`;

  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      checkin_id: payload.checkinId,
      chat_id: payload.chatId,
      action_items: payload.actionItems,
    }),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.error?.message || `Failed to update action items: ${response.status}`);
  }

  return response.json();
};

// React Query Hooks
export const useGetTrainerCheckins = (params: GetCheckinsParams, enabled: boolean = true) => {
  const currentUser = sessionStore((s) => s.currentUser);
  const trainerUid = auth.currentUser?.uid || (currentUser as { uid?: string } | null)?.uid || '';

  return useQuery({
    queryKey: ['trainer-checkins', trainerUid, params],
    queryFn: () => fetchTrainerCheckins(params),
    enabled: enabled && !!trainerUid,
    staleTime: 60 * 1000,
  });
};

export const useConfirmCheckinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: confirmTrainerCheckin,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
      queryClient.setQueryData(['checkin-detail', data.checkin.id], data.checkin);
    },
  });
};

export const useCompleteCheckinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: completeTrainerCheckin,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
      queryClient.setQueryData(['checkin-detail', data.checkin.id], data.checkin);
    },
  });
};

export const useZoomMeetingStatusQuery = (
  checkinId: string,
  chatId?: string,
  enabled: boolean = false,
) => {
  return useQuery({
    queryKey: ['zoom-meeting-status', checkinId],
    queryFn: () => fetchZoomMeetingStatus(checkinId, chatId),
    enabled: enabled && !!checkinId,
    staleTime: 30 * 1000,
    retry: 1,
  });
};

export const useSuggestCheckinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: suggestTrainerCheckin,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
      queryClient.setQueryData(['checkin-detail', data.checkin.id], data.checkin);
    },
  });
};

export const useDeclineCheckinMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: declineTrainerCheckin,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
      queryClient.setQueryData(['checkin-detail', data.checkin.id], data.checkin);
    },
  });
};

export const useSendCheckinReminderMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendTrainerCheckinReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
    },
  });
};

export const useUpdateCheckinActionItemsMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateTrainerCheckinActionItems,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
    },
  });
};

export const useGetTrainerCheckinDetails = (
  checkinId?: string,
  options?: { enabled?: boolean; targetDate?: string; chatId?: string },
) => {
  return useQuery({
    queryKey: ['checkin-detail', checkinId],
    queryFn: () => fetchTrainerCheckinDetails(checkinId!, options?.targetDate, options?.chatId),
    enabled: Boolean(checkinId) && options?.enabled !== false,
    staleTime: 30 * 1000,
  });
};

export const useMarkCheckinReadMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markTrainerCheckinRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['trainer-checkins'] });
    },
  });
};

export function enrichClientCheckin(
  slot: Record<string, any>,
  docDate: string,
  trainerUid: string,
): CheckinItem {
  const checkinProgress: CheckinProgress | undefined = slot.checkinProgress
    ? (String(slot.checkinProgress) as CheckinProgress)
    : undefined;

  const slotTime = slot.checkinTime || slot.time || '';
  const effectiveDate = slot.checkinDate || slot.date || docDate || '';

  // Canonical message doc ID determination:
  // 1. Explicit slot.message_id
  // 2. slot.id if from a chat message (message_type === 'checkin') or if it's a doc ID (not a slot time '15:00')
  const isDocId = (val?: string) =>
    Boolean(val && typeof val === 'string' && !val.includes(':') && !val.includes('/'));
  const rawId = slot.id || slot.checkin_id;
  const messageId =
    slot.message_id ||
    (slot.message_type === 'checkin' ? slot.id : undefined) ||
    (isDocId(rawId) ? rawId : undefined);

  const canonicalId =
    messageId ||
    (isDocId(slot.checkin_id) ? slot.checkin_id : undefined) ||
    (isDocId(slot.id) ? slot.id : undefined) ||
    `${effectiveDate}_${slotTime || '00:00'}`;

  const clientUid = slot.appointUId || slot.client_uid || slot.userUID || '';

  const rawStatus = slot.status || 'open';
  let appStatus: CheckinStatus = slot.app_status;
  if (!appStatus) {
    if (checkinProgress === '3') {
      appStatus = 'confirmed';
    } else if (checkinProgress === '4') {
      appStatus = 'completed';
    } else if (checkinProgress === '5' || checkinProgress === '6') {
      appStatus = 'declined';
    } else if (checkinProgress === '1' || checkinProgress === '2') {
      appStatus = 'pending_confirmation';
    } else if (rawStatus === 'close') {
      appStatus = 'completed';
    } else if (slot.isAppoint || clientUid) {
      appStatus = slot.zoom_link || slot.zoomMeetingUrl ? 'confirmed' : 'pending_confirmation';
    } else {
      appStatus = 'pending_confirmation';
    }
  }

  let statusBadge = slot.status_badge;
  let headerTitle = slot.header_title;
  if (!statusBadge || !headerTitle) {
    if (checkinProgress === '2') {
      statusBadge = {
        label: slot.checkinNewSuggest ? 'Client Counter-Proposal' : 'New Time Proposed',
        className: 'new_time_proposed',
        hasDot: true,
      };
      headerTitle = slot.checkinNewSuggest
        ? 'Client suggested another time'
        : 'New check-in time proposed';
    } else if (checkinProgress === '6') {
      statusBadge = { label: 'Cancelled', className: 'declined', hasDot: false };
      headerTitle = 'Check-in cancelled';
    } else if (appStatus === 'confirmed') {
      statusBadge = { label: 'Confirmed', className: 'confirmed', hasDot: false };
      headerTitle = 'Check-in confirmed';
    } else if (appStatus === 'completed') {
      statusBadge = { label: 'Completed', className: 'completed', hasDot: false };
      headerTitle = 'Check-in completed';
    } else if (appStatus === 'declined') {
      statusBadge = { label: 'Declined', className: 'declined', hasDot: false };
      headerTitle = 'Check-in declined';
    } else {
      statusBadge = {
        label: 'Pending confirmation',
        className: 'pending_confirmation',
        hasDot: true,
      };
      headerTitle = 'Check-in request';
    }
  }

  const checkinType: CheckinType =
    slot.checkinType || (slot.preference === 'in_person' ? 'inPerson' : 'online');
  const preference: CheckinPreference =
    slot.preference || (checkinType === 'inPerson' ? 'in_person' : 'online_zoom');
  let preferenceLabel = slot.preference_label;
  if (!preferenceLabel) {
    if (preference === 'online_zoom' || checkinType === 'online')
      preferenceLabel = 'Online via Zoom';
    else if (preference === 'in_person' || checkinType === 'inPerson')
      preferenceLabel = 'In-person';
    else if (preference === 'phone') preferenceLabel = 'Phone call';
    else preferenceLabel = preference;
  }

  let formattedDate = slot.formatted_date;
  let formattedTime = slot.formatted_time;
  if (!formattedDate || !formattedTime) {
    try {
      if (slotTime && slotTime.includes(':')) {
        const parts = slotTime.split(':');
        const hr = parseInt(parts[0], 10);
        const mn = parts[1].replace(/[^\d]/g, '');
        const isAlreadyAmPm = /AM|PM/i.test(slotTime);
        if (isAlreadyAmPm) {
          formattedTime = slotTime;
        } else {
          const ampm = hr >= 12 ? 'PM' : 'AM';
          const hr12 = hr % 12 || 12;
          formattedTime = `${hr12}:${mn.padStart(2, '0')} ${ampm}`;
        }
      } else {
        formattedTime = slotTime || '12:00 PM';
      }

      if (effectiveDate && /^\d{4}-\d{2}-\d{2}/.test(effectiveDate)) {
        const dObj = new Date(effectiveDate.slice(0, 10) + 'T00:00:00');
        formattedDate = dObj.toLocaleDateString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
        });
      } else {
        formattedDate = effectiveDate || 'Jul 23, 2026';
      }
    } catch {
      formattedDate = effectiveDate || 'Jul 23, 2026';
      formattedTime = slotTime || '12:00 PM';
    }
  }

  const formattedSubtitle =
    slot.formatted_subtitle || `${formattedDate} · ${formattedTime} · ${preferenceLabel}`;

  const clientName =
    slot.client_name && slot.client_name !== 'Client'
      ? slot.client_name
      : clientUid
        ? 'Guest'
        : 'Available slot';
  let clientInitials = slot.client_initials;
  if (!clientInitials || clientInitials === 'CL') {
    if (clientName && clientName !== 'Available slot' && clientName !== 'Guest') {
      const parts = clientName.trim().split(' ');
      if (parts.length >= 2) {
        clientInitials = (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      } else if (parts.length === 1) {
        clientInitials = parts[0].slice(0, 2).toUpperCase();
      }
    } else if (clientName === 'Guest') {
      clientInitials = 'GU';
    }
    if (!clientInitials) clientInitials = 'GU';
  }

  const zoomUrl = slot.zoomMeetingUrl || slot.zoom_link || '';
  let meetingId = slot.zoomMeetingId || slot.meeting_id || '';
  let passcode = slot.zoomMeetingPwd || slot.passcode || '';
  if (zoomUrl && (!meetingId || !passcode)) {
    const idMatch = zoomUrl.match(/\/j\/(\d+)/);
    const pwdMatch = zoomUrl.match(/[?&]pwd=([^&#]+)/);
    if (idMatch && !meetingId) {
      const rawId = idMatch[1];
      meetingId =
        rawId.length >= 10 ? `${rawId.slice(0, 3)} ${rawId.slice(3, 7)} ${rawId.slice(7)}` : rawId;
    }
    if (pwdMatch && !passcode) {
      const rawPwd = pwdMatch[1];
      passcode = rawPwd.length > 8 ? rawPwd.slice(0, 6) : rawPwd;
    }
  }

  const isAppointed = Boolean(slot.isAppoint || clientUid);
  const isRead =
    slot.is_read !== undefined
      ? Boolean(slot.is_read)
      : appStatus === 'completed' || appStatus === 'declined' || !isAppointed;

  return {
    ...slot,
    id: canonicalId,
    checkin_id: canonicalId,
    message_id: messageId || (isDocId(canonicalId) ? canonicalId : undefined),
    date: effectiveDate,
    time: slotTime,
    status: appStatus,
    legacy_status: rawStatus,
    header_title: headerTitle,
    status_badge: statusBadge,
    preference,
    preference_label: preferenceLabel,
    checkinType,
    // Mobile Checkin lifecycle fields
    checkinProgress,
    checkinDate: slot.checkinDate || effectiveDate,
    checkinTime: slot.checkinTime || slotTime,
    checkinSelectTimeType: slot.checkinSelectTimeType,
    checkinNewDate: slot.checkinNewDate || slot.suggested_date,
    checkinNewTime: slot.checkinNewTime || slot.suggested_time,
    checkinNewSelectTimeType: slot.checkinNewSelectTimeType,
    checkinNewType: slot.checkinNewType,
    checkinNewSuggest: Boolean(slot.checkinNewSuggest),
    checkinDeclinedReason: slot.checkinDeclinedReason || slot.decline_reason,
    zoomMeetingUrl: zoomUrl,
    zoomMeetingId: meetingId,
    zoomMeetingPwd: passcode,
    formatted_date: formattedDate,
    formatted_time: formattedTime,
    formatted_subtitle: formattedSubtitle,
    formatted_sent_time: slot.formatted_sent_time || '',
    isAppoint: isAppointed,
    appointUId: clientUid,
    client_uid: clientUid,
    trainer_uid: trainerUid,
    client_name: clientName,
    client_avatar:
      slot.client_avatar || slot.avatar || slot.avatarUrl || slot.profile_picture || null,
    client_email: slot.client_email || slot.email || slot.clientEmail || '',
    client_initials: clientInitials,
    zoom_link: zoomUrl,
    meeting_id: meetingId,
    passcode,
    notes: slot.notes || '',
    attachments: slot.attachments || [],
    action_items: slot.action_items || [],
    ai_progress_summary: slot.ai_progress_summary || null,
    ai_summary: slot.ai_summary || [],
    transcript_highlights: slot.transcript_highlights || [],
    full_transcript: slot.full_transcript || [],
    attendance: slot.attendance || 'attended',
    duration_mins: slot.duration_mins || 30,
    is_read: isRead,
    read_time: slot.read_time || null,
    read_by: slot.read_by || '',
    suggested_date: slot.checkinNewDate || slot.suggested_date,
    suggested_time: slot.checkinNewTime || slot.suggested_time,
    suggested_datetime_utc: slot.suggested_datetime_utc,
    suggestion_notes: slot.suggestion_notes,
    decline_reason: slot.checkinDeclinedReason || slot.decline_reason,
    created_at: slot.created_at,
    updated_at: slot.updated_at,
    status_updated_at: slot.status_updated_at,
    status_updated_by: slot.status_updated_by,
    chat_id: slot.chat_id || (clientUid ? `${trainerUid}_${clientUid}` : ''),
  } as CheckinItem;
}
